import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useQuery } from '@tanstack/vue-query';
import { computed, reactive, toValue, type MaybeRefOrGetter } from 'vue';
import { useFileManagerMounts } from '../logic/core/useFileManagerMounts';
import type { FileSystemItem } from '../types';

// Module-level singleton, populated as a side effect inside every successful queryFn call below
// (main pane AND sidebar tree rows both use this hook) — never cleared. A stale entry is harmless:
// it's discarded the moment real data or an updated count replaces it, and reading it for a path is
// a pure, always-safe lookup. This is what lets FileExplorerView size a loading skeleton to a
// folder's real child count whenever that folder was previously seen as a row anywhere, without
// threading an "expected count" through navigateTo() and racing to clear it.
const folderChildCounts = reactive(new Map<string, number>());

export const getExpectedChildCount = (path: string): number | undefined =>
  folderChildCounts.get(path);

export const useDirectoryChildrenQuery = (
  path: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  const { resolve, isNavigablePath, isSyntheticPath, syntheticListing } =
    useFileManagerMounts();

  return useQuery({
    // The virtual path alone is the cache identity — no site/project segment. Mount prefixes are
    // unique across the whole table the host supplies (a table spanning several projects prefixes each
    // one, `/1234 - Foo/CESG`), so one path can never mean two different drives and there is nothing
    // left for an extra scoping segment to disambiguate.
    queryKey: computed(() => directoryKeys.children(toValue(path))),
    // Reads the path from the query function's own `queryKey` context argument (index 2) rather
    // than closing over the outer `path` — deliberately, not a style preference. If two
    // useDirectoryChildrenQuery() calls ever transiently resolve to the same key (e.g. this call
    // and the main pane's `currentPath`-based call both starting at '/', since navigation starts
    // at the root), TanStack Query lets whichever observer's setOptions() runs last overwrite the
    // underlying query object's stored queryFn — permanently, even after that observer's own path
    // later diverges. A queryFn that closed over `path` would then keep fetching THAT OTHER
    // observer's current path forever; reading it from the query's own immutable queryKey instead
    // means a hijacked queryFn reference still fetches the correct path for whichever query object
    // actually invokes it. (Confirmed in practice: the project-root row's query permanently
    // adopted the main pane's queryFn at app boot — both start at '/' — so every subsequent
    // "invalidate root" ended up re-fetching whatever folder the main pane currently had open.)
    queryFn: async ({ queryKey }): Promise<FileSystemItem[]> => {
      const virtualPath = queryKey[2] as string;

      const target = resolve(virtualPath);
      if (!target) {
        // Not a folder on any drive: the virtual root, or an intermediate namespace level a
        // multi-project mount table implies. Answered from the mount table itself with ZERO Graph
        // calls. Deliberately skips the folderChildCounts side effect below too — a synthetic row has
        // no childCount facet to seed a loading skeleton with.
        if (isSyntheticPath(virtualPath)) return syntheticListing(virtualPath);
        // Guarded by `enabled` below; reached only if a path stopped resolving between the guard and
        // the fetch (e.g. the host's table changed and dropped a mount mid-navigation).
        throw new Error(`No accessible SharePoint library for path ${virtualPath}`);
      }

      // The single place a listing comes from a store rather than from the mount table, and therefore
      // the whole extent of this hook's coupling to Graph: `target` is a MountTarget, i.e. mount
      // coordinates already projected into Graph's vocabulary (see resolveMountPath). A mount backed
      // by another source generalizes exactly here — resolve() would return which store, and this line
      // would dispatch on it — with the branch above, `enabled` and the cache key all unchanged.
      const items = await sharePointDriveService.getChildren(target);
      for (const item of items) {
        if (item.type === 'directory' && item.childCount !== undefined) {
          folderChildCounts.set(item.path, item.childCount);
        }
      }
      return items;
    },
    // getChildren already retries transient Graph failures internally (withRetry). The
    // app-level QueryClient (main.ts) has no defaultOptions override, so it falls back to
    // vue-query's default retry:3 — stacking a second, slower retry layer on top and delaying a
    // fatal error (e.g. 404) by ~7s before it surfaces. Disable the outer layer so retries happen
    // exactly once, at the service.
    retry: false,
    // No readiness gate any more: the mount table arrives as a settled prop (D4), so a path either
    // resolves or genuinely does not belong to any mount.
    enabled: computed(() => {
      if (!toValue(enabled)) return false;
      // A path that is nowhere never fires: the alternative is a guaranteed 403/404 for something the
      // user cannot see anyway. Deliberately the very predicate useNavigationPathGuard watches — not a
      // local re-derivation of it — so the two cannot disagree, i.e. the guard cannot redirect away
      // from a path this would happily have fetched, or the reverse. See isNavigablePath for the rule.
      return isNavigablePath(toValue(path));
    }),
  });
};
